import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    connectCalendarFeedQuery,
    disconnectCalendarFeedQuery,
    getCalendarFeedsQuery,
    syncCalendarFeedQuery,
} from '../services/taskService';
import { queryKeys } from './queryKeys';
import { refreshBoardTasks } from './tasks';

// The Google Calendars connected to a board. Reading one makes, changes and removes the board's tasks all at once, so after any change here the
// board's tasks are read again as well as the list of calendars (the same as after a recurring series changes: see refreshBoardTasks).

export const calendarFeedsOptions = (boardId: string) => ({
    queryKey: queryKeys.calendarFeeds(boardId),
    queryFn: () => getCalendarFeedsQuery(boardId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
});

// `enabled: false` waits: only people who can change the board may see its calendars.
export function useCalendarFeeds(boardId: string, options: { enabled?: boolean } = {}) {
    return useQuery({ ...calendarFeedsOptions(boardId), ...(options.enabled === undefined ? {} : { enabled: options.enabled }) });
}

const refreshAfterChange = (queryClient: QueryClient, boardId: string) =>
    Promise.all([queryClient.invalidateQueries({ queryKey: queryKeys.calendarFeeds(boardId) }), refreshBoardTasks(queryClient, boardId)]);

export const connectCalendarFeedMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: ({ url, sectionId, timeZone }: { url: string, sectionId: string, timeZone: string }) => connectCalendarFeedQuery(boardId, url, sectionId, timeZone),
    onSuccess: () => refreshAfterChange(queryClient, boardId),
});

export const syncCalendarFeedMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (feedId: string) => syncCalendarFeedQuery(feedId),
    onSuccess: () => refreshAfterChange(queryClient, boardId),
});

export const disconnectCalendarFeedMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: ({ feedId, deleteTasks }: { feedId: string, deleteTasks: boolean }) => disconnectCalendarFeedQuery(feedId, deleteTasks),
    onSuccess: () => refreshAfterChange(queryClient, boardId),
});

export const useConnectCalendarFeed = (boardId: string) => useMutation(connectCalendarFeedMutation(useQueryClient(), boardId));
export const useSyncCalendarFeed = (boardId: string) => useMutation(syncCalendarFeedMutation(useQueryClient(), boardId));
export const useDisconnectCalendarFeed = (boardId: string) => useMutation(disconnectCalendarFeedMutation(useQueryClient(), boardId));
